import React from 'react';
import { CheckCircle2, ChevronRight, Clock3, Dumbbell, PlusCircle } from 'lucide-react';
import PracticeSettingsService from '../services/PracticeSettingsService';
import { t } from '../i18n';
import './PracticeSummaryProgress.css';

function safeCount(value) {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
}

export function buildPracticeSummaryCounts(cards = []) {
  return (cards || []).reduce((counts, card) => {
    if (PracticeSettingsService.isGraduated(card)) {
      counts.completed += 1;
    } else if (PracticeSettingsService.getPracticeIntensityLevel(card) === 4) {
      counts.testing += 1;
    } else {
      counts.practicing += 1;
    }
    counts.total += 1;
    return counts;
  }, { practicing: 0, testing: 0, completed: 0, total: 0 });
}

function normalizeCounts(counts = {}) {
  const practicing = safeCount(counts.practicing);
  const testing = safeCount(counts.testing);
  const completed = safeCount(counts.completed);
  return {
    practicing,
    testing,
    completed,
    total: practicing + testing + completed,
  };
}

export function buildPracticeSummarySegments(counts, completedMaxPercent = 100) {
  const { practicing, testing, completed, total } = normalizeCounts(counts);
  if (!total) return { practicing: 0, testing: 0, completed: 0 };
  const limit = Number.isFinite(completedMaxPercent) ? Math.max(0, Math.min(100, completedMaxPercent)) : 100;
  const completedPercent = Math.min(completed / total * 100, limit);
  const active = practicing + testing;
  // Keep the unallocated space neutral when no cards are still being learned.
  return {
    practicing: active ? practicing / active * (100 - completedPercent) : 0,
    testing: active ? testing / active * (100 - completedPercent) : 0,
    completed: completedPercent,
  };
}

function Stage({ className, icon: Icon, label, count, valueFormatter }) {
  return (
    <div className={`practice-summary-stage ${className}`}>
      <span className="practice-summary-stage-icon">
        <Icon size={21} strokeWidth={2.4} aria-hidden="true" />
      </span>
      <strong>{valueFormatter(count)}</strong>
      <span>{label}</span>
    </div>
  );
}

function PracticeSummaryProgress({
  counts,
  showAddMoreMessage = false,
  compact = false,
  completedMaxPercent = 100,
  valueFormatter = (value) => value.toLocaleString(),
  stageLabels = {},
  description,
  compositionLabel,
}) {
  const progress = normalizeCounts(counts);
  const segments = buildPracticeSummarySegments(progress, completedMaxPercent);

  return (
    <section className={`practice-summary-progress${compact ? ' is-compact' : ''}`} aria-label={t('learningNavigation.stats.progressAria')}>
      {!compact && <div className="practice-summary-heading">
        <div>
          <h3>{t('learningNavigation.stats.progress')}</h3>
          <p>{description ?? t('learningNavigation.stats.progressDescription')}</p>
        </div>
      </div>}

      <div className="practice-summary-flow">
        <Stage className="is-practicing" icon={Dumbbell} label={stageLabels.practicing ?? t('learningNavigation.stats.practicing')} count={progress.practicing} valueFormatter={valueFormatter} />
        <ChevronRight className="practice-summary-arrow" size={24} aria-hidden="true" />
        <Stage className="is-testing" icon={Clock3} label={stageLabels.testing ?? t('learningNavigation.stats.testing')} count={progress.testing} valueFormatter={valueFormatter} />
        <ChevronRight className="practice-summary-arrow" size={24} aria-hidden="true" />
        <Stage className="is-complete" icon={CheckCircle2} label={stageLabels.completed ?? t('learningNavigation.stats.complete')} count={progress.completed} valueFormatter={valueFormatter} />
      </div>

      <div
        className="practice-summary-composition"
        aria-label={compositionLabel ?? t('learningNavigation.stats.composition', undefined, progress)}
      >
        <span className="is-practicing" style={{ width: `${segments.practicing}%` }} />
        <span className="is-testing" style={{ width: `${segments.testing}%` }} />
        <span className="is-complete" style={{ width: `${segments.completed}%` }} />
      </div>

      {showAddMoreMessage && progress.practicing < 100 && (
        <p className="practice-summary-add-more">
          <PlusCircle size={17} strokeWidth={2.3} aria-hidden="true" />
          <span>{t('learningNavigation.stats.addMore')}</span>
        </p>
      )}
    </section>
  );
}

export default PracticeSummaryProgress;
